import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getFavouriteIds, addFavourite, removeFavourite } from '../services/favouritesService';
import { Gym } from '../types';
import { safeErrorMessage } from '../utils/sanitize';

export function useDirectoryFavourites() {
    const { user, openAuthModal } = useAuth();
    const [favouriteIds, setFavouriteIds] = useState<Set<string>>(new Set());
    const [loadingFavouriteId, setLoadingFavouriteId] = useState<string | null>(null);

    // Load favourites
    useEffect(() => {
        if (!user) {
            setFavouriteIds(new Set());
            return;
        }

        let active = true;
        getFavouriteIds(user.uid)
            .then(ids => { if (active) setFavouriteIds(new Set(ids)); })
            .catch(err => console.error("[useDirectoryFavourites] Failed to load favourites:", safeErrorMessage(err)));
        return () => { active = false; };
    }, [user]);

    const handleFavouriteToggle = useCallback(async (gym: Gym) => {
        if (!user) {
            openAuthModal({
                type: 'favourite',
                gymId: gym.id,
                // UI-57: return to WHERE THE VISITOR IS, not to a hardcoded '/gyms' —
                // which is the chooser since the flip, so the old value dumped a
                // /vic/... visitor onto "Which state are you searching in?" after auth.
                returnUrl: window.location.pathname + window.location.search
            });
            return;
        }

        const isFavourite = favouriteIds.has(gym.id) || (!!gym.google_place_id && favouriteIds.has(gym.google_place_id));
        const activeId = favouriteIds.has(gym.id) ? gym.id : (gym.google_place_id || gym.id);
        setLoadingFavouriteId(gym.id);

        try {
            if (isFavourite) {
                await removeFavourite(user.uid, activeId);
                setFavouriteIds(prev => {
                    const next = new Set(prev);
                    next.delete(activeId);
                    return next;
                });
            } else {
                const newId = gym.stableId || gym.id;
                await addFavourite(user.uid, newId, 'gym');
                setFavouriteIds(prev => {
                    const next = new Set(prev);
                    next.add(newId);
                    return next;
                });
            }
        } catch (error) {
            console.error("[useDirectoryFavourites] Toggle failed:", safeErrorMessage(error));
        } finally {
            setLoadingFavouriteId(null);
        }
    }, [user, favouriteIds, openAuthModal]);

    return {
        favouriteIds,
        handleFavouriteToggle,
        loadingFavouriteId
    };
}
